// Constants
import { DefaultOrderHint } from "@ms/uno-constants/lib/local/OrderHintConstants";
// Models
import { Temporal } from "temporal-polyfill-lite";
import { ChecklistItemExtensionKeys } from "../../../service/planner/extensions/IChecklistExtensionsResource";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
/**
 * The ChecklistItem implementation.
 */ export class ChecklistItem extends PatchableAndDeltaSyncableODataEntity {
    mapEntityChangesToResource(changed, source) {
        return {
            displayName: changed.displayName,
            isChecked: changed.isChecked,
            extensions: changed.extensions && this.serializeExtensions()
        };
    }
    mapDeltaChangesToEntity(delta) {
        return this.processDeltaWithTranslators(delta, {
            completedDateTime: (value)=>value ? Temporal.Instant.from(value) : null,
            extensions: (value)=>ChecklistItem.deserializeExtensions(value),
            lastModifiedDateTime: (value)=>value ? Temporal.Instant.from(value) : null
        });
    }
    /**
     * Creates a new checklist item from the resource.
     * @param resource The resource to create the checklist item from.
     */ static fromResource(resource) {
        return new ChecklistItem({
            id: resource.id,
            version: resource["@etag"],
            displayName: resource.displayName,
            extensions: this.deserializeExtensions(resource.extensions),
            lastModifiedBy: resource.lastModifiedBy,
            lastModifiedDateTime: Temporal.Instant.from(resource.lastModifiedDateTime),
            isChecked: resource.isChecked,
            completedBy: resource.completedBy ? resource.completedBy : null,
            completedDateTime: resource.completedDateTime ? Temporal.Instant.from(resource.completedDateTime) : null
        });
    }
    /**
     * Deserializes the extensions of the checklist item.
     * The values must be serializable to JSON.
     */ static deserializeExtensions(extensions) {
        const output = {
            orderHint: DefaultOrderHint
        };
        // First parse the legacy data if it exists
        const serializedLegacyData = extensions[ChecklistItemExtensionKeys.legacyData];
        if (serializedLegacyData) {
            try {
                const legacyExtensionData = JSON.parse(serializedLegacyData);
                output.orderHint = legacyExtensionData.orderHint;
            } catch (parseError) {
                throw new Error(`Deserialization Error: [Entity=${this.name}][Field=Extensions][Key=legacyData]`);
            }
        }
        // No custom extensions for checklist items yet so just return the deserialized legacy data for now
        return output;
    }
    getClone() {
        return new ChecklistItem({
            id: this.id,
            version: this.version,
            displayName: this.displayName,
            extensions: this.extensions,
            lastModifiedBy: this.lastModifiedBy,
            lastModifiedDateTime: this.lastModifiedDateTime,
            isChecked: this.isChecked,
            completedBy: this.completedBy,
            completedDateTime: this.completedDateTime
        });
    }
    setProperty(key, value) {
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    forCreate() {
        return {
            displayName: this.displayName,
            isChecked: this.isChecked,
            extensions: this.serializeExtensions()
        };
    }
    forSerialization() {
        return {
            id: this.id,
            "@etag": this.version,
            displayName: this.displayName,
            lastModifiedBy: this.lastModifiedBy,
            lastModifiedDateTime: this.lastModifiedDateTime.toString(),
            isChecked: this.isChecked,
            completedBy: this.completedBy,
            completedDateTime: this.completedDateTime?.toString() ?? null,
            extensions: this.serializeExtensions()
        };
    }
    getOrderHint() {
        return this.extensions.orderHint;
    }
    setOrderHint(value) {
        const updatedExtensions = {
            ...this.extensions,
            orderHint: value
        };
        const clone = this.getClone();
        clone.extensions = updatedExtensions;
        return clone;
    }
    /**
     * Serializes the extensions of the checklist item.
     * The values must be serializable to JSON.
     */ serializeExtensions() {
        const output = {};
        // Always serialize to both Legacy and Evo formats to support round-trip deserialization across v3 and v4 apis
        const legacyData = {
            orderHint: this.extensions.orderHint
        };
        output[ChecklistItemExtensionKeys.legacyData] = JSON.stringify(legacyData);
        // No custom extensions for checklist items yet so just return the serialized legacy data for now
        return output;
    }
    constructor(propertyBag){
        super(propertyBag);
        this.displayName = propertyBag.displayName;
        this.extensions = propertyBag.extensions ?? {
            orderHint: DefaultOrderHint
        };
        this.lastModifiedBy = propertyBag.lastModifiedBy;
        this.lastModifiedDateTime = propertyBag.lastModifiedDateTime ?? Temporal.Now.instant();
        this.isChecked = propertyBag.isChecked ?? false;
        this.completedBy = propertyBag.completedBy ?? null;
        this.completedDateTime = propertyBag.completedDateTime ?? null;
    }
}
